// Живые ссылки и сноски: отображения узлов cite / xref / footnote / structHeading, список целей ссылок
// (с миниатюрами), варианты фраз («на рисунке 3», «(приложение А)»), диалоги перекрёстной ссылки и сноски.
import { useEffect, useMemo, useRef, useState } from 'react';
import { NodeViewWrapper, type NodeViewProps, type JSONContent } from '@tiptap/react';
import type { Ctx, DialogProps } from '../../core/registry';
import type { Numbering, NumInfo } from '../../core/numbering';
import { useNum, useNumbering } from '../../core/editor/numberingPlugin';
import { citeText } from '../../core/numbering';
import { typoText, nbspParts } from '../pages/nbsp';
import { STRUCT_KINDS } from '../../core/schema/constants';
import { MiniEditor } from '../../core/editor/MiniEditor';
import { ctx as globalCtx } from '../../core/ctx';
import { appStore, useApp } from '../../core/store';
import { Btn, Modal } from '../../ui/controls';
import { Icon } from '../../ui/icons';
import { prefs } from './profile';
import { NB } from './data';

// ------------------------------------------------------------------------------------------------ отображения
export function CiteView({ node, selected, updateAttributes }: NodeViewProps) {
  const nb = useNumbering();
  const sources = useApp((s) => s.sources);
  const text = citeText(node.attrs.items || [], nb);
  // ссылка на источник, дописанная импортом .docx (items[].auto - stoweb/autorefs.ensure_cites): выделена, двойной
  // щелчок - «принять» (выделение снимается), следующий - изменить
  const auto = (node.attrs.items || []).some((it: any) => it.auto);
  const tip = (node.attrs.items || []).map((it: any) => {
    const s = sources.find((x) => x.id === it.source);
    return s ? `${nb.sources.get(s.id)}. ${(s.text || s.formatted || s.title || '').slice(0, 140)}${it.loc ? ' - ' + it.loc : ''}${it.auto ? ' (добавлено автоматически)' : ''}` : 'Источник удалён из библиотеки';
  }).join('\n');
  return (
    <NodeViewWrapper as="span" className={'sto-cite' + (selected ? ' is-selected' : '') + (text === '[?]' ? ' is-bad' : '') + (auto ? ' is-auto' : '')}
      title={tip + (auto ? '\nДобавлено автоматически при загрузке файла: на источник не было ссылки в тексте - проверьте, что его сведения использованы здесь; двойной щелчок - принять'
        : '\nДвойной щелчок - изменить')}
      onDoubleClick={() => {
        if (auto) updateAttributes({ items: (node.attrs.items || []).map(({ auto: _a, ...it }: any) => it) });
        else globalCtx.run('refs.editCite');
      }}>{text}</NodeViewWrapper>
  );
}

export function XrefView({ node, selected, updateAttributes }: NodeViewProps) {
  const info = useNum(node.attrs.target);
  // ссылка, дописанная импортом .docx (attrs.auto): выделена, двойной щелчок - «принять» (выделение снимается)
  const auto = !!node.attrs.auto;
  return (
    <NodeViewWrapper as="span" className={'sto-xref' + (selected ? ' is-selected' : '') + (!info ? ' is-bad' : '') + (auto ? ' is-auto' : '')}
      data-target={node.attrs.target}
      title={(info ? `${info.label || info.title}${info.title && info.label ? '. ' + info.title : ''}\nCtrl+щелчок - перейти` : 'Объект ссылки удалён - удалите ссылку или вставьте новую (@)')
        + (auto ? '\nДобавлено автоматически при загрузке файла - проверьте формулировку; двойной щелчок - принять' : '')}
      onClick={(e) => { if ((e.ctrlKey || e.metaKey) && info) { e.preventDefault(); globalCtx.run('sto.gotoNode', node.attrs.target); } }}
      onDoubleClick={() => { if (auto) updateAttributes({ auto: false }); }}
    >{info ? info.ref : '??'}</NodeViewWrapper>
  );
}

export function FootnoteView({ node, selected, updateAttributes }: NodeViewProps) {
  const nb = useNumbering();
  const n = nb.footnotes.get(node.attrs.id) ?? '*';
  const text = plain(node.attrs.content || []);
  return (
    <NodeViewWrapper as="sup" className={'sto-fn' + (selected ? ' is-selected' : '') + (!text.trim() ? ' is-bad' : '')}
      title={(text || 'Пустая сноска') + '\nДвойной щелчок - изменить'}
      onDoubleClick={async () => {
        const res = await globalCtx.openDialog<JSONContent[]>('sto.footnote', { content: node.attrs.content, num: n });
        if (res) updateAttributes({ content: res });
      }}>{n}</NodeViewWrapper>
  );
}

export function plain(nodes: JSONContent[]): string {
  return (nodes || []).map((n) => (n.type === 'text' ? n.text : n.type === 'mathInline' ? n.attrs?.latex : '')).join('');
}

export function StructHeadingView({ node, selected }: NodeViewProps) {
  const nb = useNumbering();
  const title = STRUCT_KINDS[node.attrs.kind] || node.attrs.kind;
  return (
    <NodeViewWrapper className={'sto-struct' + (selected ? ' is-selected' : '')} data-kind={node.attrs.kind} data-drag-handle="">
      <div className="sto-struct-title" contentEditable={false}>{title}</div>
      {node.attrs.kind === 'sources' && (
        <div className="sto-sources-list" contentEditable={false}>
          {nb.sourceList.length ? nb.sourceList.map(([id, text], i) => (
            <p key={id} className="sto-source-item" data-source={id}>{nbspParts(`${i + 1} ${typoText(text)}`).map((x, k) => (x.nb ? <span key={k} className="pg-nb">{x.text}</span> : x.text))}</p>
          )) : <p className="muted sto-src-empty">Список заполняется автоматически из библиотеки источников.{' '}
            <button type="button" className="link-btn" onClick={() => globalCtx.openDialog('sto.source', {})}>Добавить источник</button>{' · '}
            <button type="button" className="link-btn" onClick={() => globalCtx.openDialog('sto.sourceImport', {})}>Импорт BibTeX / RIS / списка</button></p>}
        </div>
      )}
    </NodeViewWrapper>
  );
}

// ------------------------------------------------------------------------------------------------ цели и фразы
export type TargetKind = 'fig' | 'tbl' | 'eq' | 'lst' | 'app' | 'sec' | 'struct';
export const KIND_NAMES: Record<string, string> = {
  fig: 'Рисунки', tbl: 'Таблицы', eq: 'Формулы', lst: 'Листинги', app: 'Приложения', sec: 'Разделы', struct: 'Структурные элементы',
};

/** Варианты фразы: [id, слово до номера, текст после, подпись]. Слово и номер - через пробел NB (неразрывный только при USE_NBSP). */
export const PHRASES: Record<string, [string, string, string][]> = {
  fig: [['word', 'рисунке', ''], ['na', 'на рисунке', ''], ['paren', '(рисунок', ')'], ['nom', 'рисунок', ''], ['gen', 'рисунка', ''], ['dat', 'рисунку', ''], ['num', '', '']],
  tbl: [['word', 'таблице', ''], ['v', 'в таблице', ''], ['paren', '(таблица', ')'], ['nom', 'таблица', ''], ['gen', 'таблицы', ''], ['dat', 'таблице', ''], ['num', '', '']],
  eq: [['word', 'формуле', ''], ['po', 'по формуле', ''], ['gen', 'формулы', ''], ['iz', 'из формулы', ''], ['dat', 'формуле', ''], ['num', '', '']],
  lst: [['paren', '(листинг', ')'], ['word', 'листинге', ''], ['v', 'в листинге', ''], ['nom', 'листинг', ''], ['gen', 'листинга', ''], ['dat', 'листингу', ''], ['num', '', '']],
  app: [['paren', '(приложение', ')'], ['word', 'приложении', ''], ['v', 'в приложении', ''], ['nom', 'приложение', ''], ['gen', 'приложения', ''], ['dat', 'приложению', ''], ['num', '', '']],
  sec: [['word', 'разделе', ''], ['v', 'в разделе', ''], ['paren', '(см. раздел', ')'], ['nom', 'раздел', ''], ['gen', 'раздела', ''], ['dat', 'разделу', ''], ['num', '', '']],
  struct: [['num', '', '']],
};
export const SRC_KIND = 'src';

export interface Target { id: string; kind: TargetKind; info: NumInfo; thumb?: string; text: string }

/** Все цели ссылок по порядку документа. */
export function allTargets(nb: Numbering): Target[] {
  const doc = appStore.getState().editor?.state.doc;
  const pid = appStore.getState().projectId;
  const srcById = new Map<string, string>();
  doc?.descendants((n) => { if (n.type.name === 'figure' && n.attrs.id) srcById.set(n.attrs.id, n.attrs.src); return !n.isLeaf; });
  const out: Target[] = [];
  for (const id of nb.order) {
    const info = nb.byId.get(id);
    if (!info) continue;
    if (info.kind === 'sec' && !info.num) continue;          // подзаголовки без номера (в структурных элементах)
    if (id === 'meta_ai') continue;
    const t: Target = { id, kind: info.kind as TargetKind, info, text: `${info.label} ${info.title}`.toLowerCase() };
    if (info.kind === 'fig' && pid && srcById.get(id)) t.thumb = globalCtx.api.assetUrl(pid, srcById.get(id)!, 96);
    out.push(t);
  }
  return out;
}

/** Строка для показа фразы: «на рисунке 3». */
export function phraseText(kind: string, phraseId: string, info: NumInfo): string {
  const ph = (PHRASES[kind] || PHRASES.struct).find((p) => p[0] === phraseId) || PHRASES[kind]?.[0];
  if (!ph || ph[0] === 'num' || !ph[1]) return info.ref;
  return `${ph[1]}${NB}${info.ref}${ph[2]}`;
}

/** Содержимое для вставки: текст фразы + живая ссылка (номер обновляется сам). */
export function refContent(kind: string, phraseId: string, targetId: string): JSONContent[] {
  const ph = (PHRASES[kind] || PHRASES.struct).find((p) => p[0] === phraseId);
  const out: JSONContent[] = [];
  if (ph && ph[1]) out.push({ type: 'text', text: ph[1] + NB });
  out.push({ type: 'xref', attrs: { target: targetId } });
  if (ph && ph[2]) out.push({ type: 'text', text: ph[2] });
  return out;
}

/** Фраза по умолчанию: если слово уже набрано («на рисунке ») - только номер, иначе - по предлогу или последний выбор. */
export function defaultPhrase(kind: string, before: string): string {
  const b = before.toLowerCase();
  const words: Record<string, RegExp> = {
    fig: /(рисун[а-яё]*|рис\.)[\s ]*$/, tbl: /(таблиц[а-яё]*|табл\.)[\s ]*$/, eq: /(формул[а-яё]*)[\s ]*$/,
    lst: /(листинг[а-яё]*)[\s ]*$/, app: /(приложени[а-яё]*)[\s ]*$/, sec: /(раздел[а-яё]*|подраздел[а-яё]*|пункт[а-яё]*)[\s ]*$/,
  };
  if (words[kind]?.test(b)) return 'num';
  const ok = (id: string) => !!PHRASES[kind]?.some((p) => p[0] === id);
  // «см.», «см. также» - именительный падеж без скобок: «см. также приложение А» (у формулы - номер «(1)»)
  if (/(?:^|[\s( ])см\.(?:[\s ]+также)?[\s ]*$/.test(b)) return kind === 'eq' || !ok('nom') ? 'num' : 'nom';
  // падеж по предлогу: «на рисунке», «по рисунку», «из таблицы», «к приложению»
  const prep = (b.match(/(?:^|[\s(])(на|в|во|о|об|при|по|к|ко|согласно|из|до|для|от|у|без|после|вместо|кроме)[\s ]*$/) || [])[1];
  if (prep) {
    const want = /^(по|к|ко|согласно)$/.test(prep) ? 'dat' : /^(из|до|для|от|у|без|после|вместо|кроме)$/.test(prep) ? 'gen' : 'word';
    if (ok(want)) return want;
    if (ok('word')) return 'word';
  }
  // в скобках: «(рисунок 3)», «(приложение А)», у формулы - только номер «(1)»
  if (/\($/.test(b)) return kind === 'eq' || !ok('nom') ? 'num' : 'nom';
  const saved = prefs.phrase(kind);
  if (saved && PHRASES[kind]?.some((p) => p[0] === saved)) return saved;
  // формула без предлога («имеет вид (1)», «выражением (2)») - только номер в скобках
  if (kind === 'eq') return 'num';
  return PHRASES[kind]?.[0]?.[0] || 'num';
}

export function insertRef(c: Ctx, kind: string, phraseId: string, targetId: string, range?: { from: number; to: number }) {
  const ed = c.editor;
  if (!ed) return;
  prefs.setPhrase(kind, phraseId);
  const content = refContent(kind, phraseId, targetId);
  const ch = ed.chain().focus();
  (range ? ch.insertContentAt(range, content) : ch.insertContent(content)).run();
}

/** Текст перед курсором в текущем абзаце. */
export function textBeforeCursor(c: Ctx, max = 40): string {
  const ed = c.editor;
  if (!ed) return '';
  const $f = ed.state.selection.$from;
  return $f.parent.textBetween(Math.max(0, $f.parentOffset - max), $f.parentOffset, ' ', '￼');
}

// ------------------------------------------------------------------------------------------------ диалог ссылки
export function TargetThumb({ t }: { t: Target }) {
  if (t.thumb) return <img className="xref-thumb" src={t.thumb} alt="" loading="lazy" />;
  const icon = { fig: 'image', tbl: 'table', eq: 'formula', lst: 'braces', app: 'attach', sec: 'h1', struct: 'bookmark' }[t.kind] || 'xref';
  return <span className="xref-thumb xref-thumb-ico"><Icon name={icon} size={18} /></span>;
}

export function XrefDialog({ ctx, close }: DialogProps) {
  const nb = useNumbering();
  const targets = useMemo(() => allTargets(nb), [nb]);
  const before = useMemo(() => textBeforeCursor(ctx), []);
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    targets.forEach((t) => { m[t.kind] = (m[t.kind] || 0) + 1; });
    return m;
  }, [targets]);
  const firstKind = (['fig', 'tbl', 'eq', 'lst', 'app', 'sec'] as const).find((k) => counts[k]) || 'fig';
  const [kind, setKind] = useState<string>(firstKind);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [phrase, setPhrase] = useState<string>(defaultPhrase(firstKind, before));
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setPhrase(defaultPhrase(kind, before)); setSel(0); }, [kind]);
  const match = (t: Target) => !q || t.text.includes(q.toLowerCase());
  const inKind = targets.filter((t) => t.kind === kind && match(t));
  // поиск не нашёл ничего на текущей вкладке - показываем совпадения всех видов (раздел, приложение…)
  const shown = q && !inKind.length ? targets.filter(match) : inKind;
  const cur = shown[Math.min(sel, shown.length - 1)];
  const insert = (t?: Target) => {
    if (!t) return;
    insertRef(ctx, t.kind, t.kind === kind ? phrase : defaultPhrase(t.kind, before), t.id);
    close(t.id);
  };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, shown.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); insert(cur); }
  };
  return (
    <Modal title="Перекрёстная ссылка" onClose={() => close()} width={760} testid="xref-dialog" footer={<>
      <span className="muted small">Совет: в тексте наберите <b>@</b> - ссылка вставляется без диалога</span>
      <span className="topbar-spacer" />
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Вставить" primary onClick={() => insert(cur)} disabled={!cur} testid="xref-ok" />
    </>}>
      <div className="tabs">
        {(['fig', 'tbl', 'eq', 'lst', 'app', 'sec'] as const).map((k) => (
          <button key={k} type="button" className={'tab' + (kind === k ? ' is-active' : '')} onClick={() => { setKind(k); inputRef.current?.focus(); }}
            data-testid={'xref-kind-' + k}>
            {KIND_NAMES[k]} <span className="tab-count">{counts[k] || 0}</span></button>
        ))}
      </div>
      <input ref={inputRef} className="search" placeholder="Поиск по номеру или названию…" value={q} autoFocus
        onChange={(e) => { setQ(e.target.value); setSel(0); }} onKeyDown={onKey} data-testid="xref-search" />
      <div className="xref-list" onKeyDown={onKey}>
        {!shown.length && <div className="muted pad">{counts[kind] ? 'Ничего не найдено.' : `Нет объектов: ${KIND_NAMES[kind].toLowerCase()}.`}</div>}
        {shown.map((t, i) => (
          <button key={t.id} type="button" className={'xref-row' + (t === cur ? ' is-sel' : '')} onMouseEnter={() => setSel(i)}
            onClick={() => insert(t)} data-testid="xref-target">
            <TargetThumb t={t} />
            <b>{t.kind === 'sec' ? t.info.num : t.info.label || t.info.ref}</b>
            <span className="xref-title">{t.kind === 'eq' ? <code>{t.info.title}</code> : (t.info.title || <span className="muted">без названия</span>)}</span>
          </button>
        ))}
      </div>
      {cur && (
        <div className="phrase-pick" data-testid="xref-phrases">
          <span className="muted">Вставить как:</span>
          {(PHRASES[kind] || []).map(([id]) => (
            <button key={id} type="button" className={'chip' + (phrase === id ? ' is-active' : '')} onClick={() => setPhrase(id)}
              data-phrase={id}>{phraseText(kind, id, cur.info)}</button>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ сноска
export function FootnoteDialog({ props, close }: DialogProps) {
  const [content, setContent] = useState<JSONContent[]>(props.content || []);
  const text = plain(content).trim();
  const dot = /\.$/.test(text) && !/\.\.$|(?:^|\s)\S\.$/.test(text);
  return (
    <Modal title={props.num ? `Сноска ${props.num}` : 'Сноска'} onClose={() => close()} width={620} testid="footnote-dialog" footer={<>
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Готово" primary onClick={() => close(dot ? stripDot(content) : content)} testid="footnote-ok" disabled={!text} />
    </>}>
      <p className="muted small">Текст сноски размещается внизу страницы 10 пт; номер ставится автоматически (4.10.9–4.10.11).
        Точку в конце не ставят - редактор уберёт её сам. Ctrl+Enter - готово.</p>
      <div onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && text) { e.preventDefault(); close(dot ? stripDot(content) : content); } }}>
        <MiniEditor mode="inline" value={content} onChange={setContent} placeholder="Текст сноски" testid="footnote-editor" minHeight={40} />
      </div>
      {dot && <div className="hint-warn small">Точка в конце сноски будет убрана (4.10, FOOTNOTE-DOT).</div>}
    </Modal>
  );
}

export function stripDot(content: JSONContent[]): JSONContent[] {
  const c = JSON.parse(JSON.stringify(content || [])) as JSONContent[];
  for (let i = c.length - 1; i >= 0; i--) {
    const n = c[i];
    if (n.type !== 'text') break;
    const t = (n.text || '').replace(/\s+$/, '');
    if (!t) { c.splice(i, 1); continue; }
    if (t.endsWith('.') && !t.endsWith('..')) n.text = t.slice(0, -1);
    break;
  }
  return c;
}
